/**
 * Why a message never reached the assistant, in words, from a stored row.
 *
 * At send time the backend hands back a sentence with the refusal and the interface
 * renders that verbatim. A row read back from history carries only `errorCode`, so the
 * wording has to be reconstructed here — the same sentences, kept in step with
 * `error.code.ts` deliberately rather than invented, because a user comparing what they
 * saw at the time with what the transcript says later must not find two different
 * explanations of one event.
 *
 * Matched on the numeric suffix rather than the whole code. The category prefix is
 * assigned server-side and has changed once already; the suffix is what identifies the
 * gate, and a prefix change must not silently turn every refusal into "not sent".
 */
const REASONS = {
  "001": "You do not have access to the AI workspace",
  "002":
    "Your role has no AI usage limits configured. Ask an administrator to assign a quota profile.",
  "003": "The AI workspace was turned off",
  "010": "You had sent too many messages in a short period",
  "011": "Your monthly AI usage allowance had been reached",
  "012": "That message is longer than your allowed message length",
  "016": "Your monthly AI spending limit had been reached",
  "017": "The organisation's monthly AI spending limit had been reached",
  "030": "That conversation could not be found",
  "040":
    "This message was not sent because it appears to contain sensitive data. It was not saved.",
  "041": "The safety check was unavailable, so the message could not be sent",
  "050": "The AI workspace is not configured",
  "051": "The assistant could not complete this request",
  "052": "The assistant took too long to respond",
  "053": "The assistant was busy",
  // Neither of these is a failure of the answer, and wording them as one sends people to
  // the wrong conclusion. A deploy landed while the model was answering perfectly well
  // (054), or landed just before it started (055). In both cases resending works, and
  // that is the whole content of the message.
  "054": "The assistant was interrupted by a system update",
  "055": "The workspace was restarting",
};

/** The sensitive-data block: the boundary doing its job, not a failure. */
const BLOCK_SUFFIX = "040";

/**
 * The suffix of an ARCH error code, e.g. "016" from "E1018-016".
 *
 * @param {String|null} code
 * @returns {String|null}
 */
export function errorCodeSuffix(code) {
  if (!code) {
    return null;
  }
  const match = String(code).match(/-(\d{3})$/);
  return match ? match[1] : null;
}

/**
 * The sentence for a code, or null when this build has never heard of it.
 *
 * Separate from `describeRefusal` because the two callers want different fallbacks: a
 * user row that did not send says so, an assistant row that failed after reaching the
 * provider says something else entirely.
 *
 * @param {String|null} code
 * @returns {String|null}
 */
export function reasonForCode(code) {
  const suffix = errorCodeSuffix(code);
  return (suffix && REASONS[suffix]) || null;
}

/**
 * Describe a message that did not go through.
 *
 * Handles both shapes the backend produces: a `blocked` row carrying a filter category,
 * and a `refused` or `failed` row carrying an error code. `refused` and `failed` are
 * read the same way on purpose — user rows were marked `failed` before the status enum
 * gained `refused`, and history written under the old value has to keep reading
 * correctly.
 *
 * @param {Object} message - a message row
 * @returns {Object|null} { message, code, isBlock } or null when nothing went wrong
 */
export function describeRefusal(message) {
  if (!message) {
    return null;
  }

  if (message.blockReason) {
    return {
      message: `${REASONS[BLOCK_SUFFIX]} (${message.blockReason})`,
      code: message.errorCode || null,
      isBlock: true,
    };
  }

  if (message.status !== "refused" && message.status !== "failed") {
    return null;
  }

  const suffix = errorCodeSuffix(message.errorCode);

  return {
    message:
      reasonForCode(message.errorCode) ||
      // No code, or one this build has never heard of. Saying plainly that it did not
      // send beats guessing at a reason: the code is printed beside it either way, and
      // that is what makes a screenshot worth forwarding.
      "This message was not sent",
    code: message.errorCode || null,
    isBlock: suffix === BLOCK_SUFFIX,
  };
}

export default describeRefusal;
